// Mermaid diagram rendering for agent replies — extracted from
// AgentDetail.tsx. beautiful-mermaid (~2.5 MB elkjs layout engine) is
// lazy-loaded on the first diagram render, so sessions that never show a
// diagram don't pay the bundle cost.
//
// Exports: MermaidBlock (the rendered diagram component) and
// autoWrapMermaid (wraps bare flowchart/graph text the TUI emitted
// without fences into ```mermaid blocks).

import { useState, useEffect, memo } from 'react';
import { diag } from '../lib/diag';

type MermaidModule = typeof import('beautiful-mermaid');
let mermaidMod: MermaidModule | null = null;
let mermaidLoadPromise: Promise<MermaidModule> | null = null;
function loadMermaid(): Promise<MermaidModule> {
  if (!mermaidLoadPromise) {
    // Instrument the one-time chunk fetch (~3.5 MB built). On a cold/slow
    // network this is the prime suspect for "first agent with a diagram
    // takes 10-30s, then fast" — the chunk caches after the first load
    // (Alex 2026-06-18). diag logs the real wall-clock so we measure it.
    const t0 = performance.now();
    diag('mermaid chunk: load start (~3.5MB, first diagram render)');
    mermaidLoadPromise = import('beautiful-mermaid').then((m) => {
      mermaidMod = m;
      diag('mermaid chunk: loaded', { loadMs: Math.round(performance.now() - t0) });
      return m;
    });
  }
  return mermaidLoadPromise;
}

// A bare mermaid block from the TUI starts with one of these on its own
// first line — the keyword (optional direction), no trailing prose.
const MERMAID_DIRECTION = /^(flowchart|graph)\s+(TD|TB|LR|RL|BT)\s*$/;
const MERMAID_KEYWORD = /^(sequenceDiagram|stateDiagram(?:-v2)?|classDiagram|erDiagram|gantt|pie|journey|gitGraph|mindmap|timeline|quadrantChart|requirementDiagram)\s*$/;
// Continuation detection: arrows (-->) or lines starting with mermaid-
// specific keywords. Lets us greedy-consume paragraphs that the TUI
// split with blank lines back into one block until prose returns.
const MERMAID_CONTINUATION = /-->|---(?:[^-]|$)|^\s*(?:style|classDef|linkStyle|subgraph|end|click|direction)\s/m;

export function autoWrapMermaid(md: string): string {
  if (!md.includes('\n')) return md;
  if (md.includes('```mermaid')) return md;
  const paragraphs = md.split(/\n{2,}/);
  const out: string[] = [];
  let changed = false;
  let i = 0;
  while (i < paragraphs.length) {
    let p = paragraphs[i];
    // Variante B: Das Label „mermaid" ist ein EIGENER Absatz und der Body
    // folgt im nächsten — dann Label verwerfen und mit dem Body weitermachen.
    if (/^\s*mermaid\s*$/i.test(p) && i + 1 < paragraphs.length) {
      const nextFirst = (paragraphs[i + 1].split('\n').find((l) => l.trim()) || '').trim();
      if (MERMAID_DIRECTION.test(nextFirst) || MERMAID_KEYWORD.test(nextFirst)) {
        i += 1;
        p = paragraphs[i];
        changed = true;
      }
    }
    // Das Terminal rendert einen ```mermaid-Fence als eigene Zeile „mermaid"
    // + eingerückten Body — die Backticks selbst kommen nie im Pane an
    // (Alex 17.08., Screenshot 119744: „mermaid graph TB START[…]" als
    // Fließtext). Erste NICHT-leere Zeile ist dann das Sprach-Label, nicht
    // der Diagramm-Start; wir überspringen es für die Erkennung und werfen es
    // beim Wrap weg (es gehört zur Fence, nicht zur Grafik).
    const lines = p.split('\n');
    const nonEmpty = lines.map((l) => l.trim()).filter(Boolean);
    let firstLine = nonEmpty[0] || '';
    if (/^mermaid$/i.test(firstLine) && nonEmpty.length > 1) {
      firstLine = nonEmpty[1];
      if (MERMAID_DIRECTION.test(firstLine) || MERMAID_KEYWORD.test(firstLine)) {
        const idx = lines.findIndex((l) => /^\s*mermaid\s*$/i.test(l));
        p = [...lines.slice(0, idx), ...lines.slice(idx + 1)].join('\n');
      }
    }
    const isStart = MERMAID_DIRECTION.test(firstLine) || MERMAID_KEYWORD.test(firstLine);
    if (!isStart) {
      out.push(paragraphs[i]);
      i++;
      continue;
    }
    // Greedy-consume: any subsequent paragraph that still looks like
    // mermaid (arrows or style/subgraph/etc keywords) belongs to the
    // same diagram. Stop as soon as a prose paragraph appears.
    const block = [p];
    let j = i + 1;
    while (j < paragraphs.length && MERMAID_CONTINUATION.test(paragraphs[j])) {
      block.push(paragraphs[j]);
      j++;
    }
    out.push('```mermaid\n' + block.join('\n\n').trimEnd() + '\n```');
    changed = true;
    i = j;
  }
  return changed ? out.join('\n\n') : md;
}

// Module-level cache keyed by raw source string. Survives unmount /
// re-mount cycles caused by ReactMarkdown rebuilding its children
// array on every parent re-render (poll tick, sibling update, …).
// Without this, the same diagram was being re-rendered through elkjs
// on every poll, even when nothing changed.
type MermaidRenderResult = { svg: string; error: string | null };
const MERMAID_CACHE = new Map<string, MermaidRenderResult>();

function renderMermaidCached(source: string): MermaidRenderResult {
  const hit = MERMAID_CACHE.get(source);
  if (hit) return hit;
  // Module not loaded yet — caller (MermaidBlock) kicks off the lazy
  // import and re-renders when it lands. Don't cache this sentinel.
  if (!mermaidMod) return { svg: '', error: null };
  // Markdown engine HTML-escapes &, <, >, " — elkjs chokes on these
  // in node labels, so decode back to raw mermaid source.
  const decoded = source
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
  let result: MermaidRenderResult;
  try {
    const colors = {
      ...mermaidMod.THEMES['github-light'],
      bg: 'transparent',
      // Bare family name only — beautiful-mermaid feeds this into a Google-Fonts
      // css2 URL via encodeURIComponent, so a full stack ("Roboto, sans-serif")
      // becomes "Roboto%2C%20sans-serif" → 400 Bad Request. (Alex 2026-06-29)
      font: 'Roboto',
    };
    result = { svg: mermaidMod.renderMermaidSVG(decoded, colors), error: null };
  } catch (e) {
    result = { svg: '', error: e instanceof Error ? e.message : String(e) };
  }
  // Bound cache size so a long session with many evolving diagrams
  // doesn't grow without limit. LRU-ish: drop oldest 32 when > 128.
  if (MERMAID_CACHE.size > 128) {
    const keys = Array.from(MERMAID_CACHE.keys()).slice(0, 32);
    for (const k of keys) MERMAID_CACHE.delete(k);
  }
  MERMAID_CACHE.set(source, result);
  return result;
}

// React.memo so this component skips re-renders when `source` is
// reference-equal to last time — covers the common case where the
// parent re-rendered but the diagram text didn't change.
export const MermaidBlock = memo(function MermaidBlock({ source }: { source: string }) {
  // Lazy-load gate: until beautiful-mermaid is fetched, show the raw
  // source as a code block (same as the error-fallback). The state flip
  // triggers a re-render once the module lands, at which point
  // renderMermaidCached produces real SVG.
  const [modReady, setModReady] = useState(!!mermaidMod);
  useEffect(() => {
    if (modReady) return;
    let cancelled = false;
    loadMermaid().then(() => { if (!cancelled) setModReady(true); });
    return () => { cancelled = true; };
  }, [modReady]);
  const { svg, error } = renderMermaidCached(source);
  if (!modReady) {
    return <pre className="code-block mermaid-fallback">{source}</pre>;
  }
  if (error) {
    return (
      <pre className="code-block mermaid-fallback" title={`mermaid render failed: ${error}`}>
        {source}
      </pre>
    );
  }
  // FOUC fix: SVG's embedded <style> block is parsed async by the
  // browser. For a frame or two the geometry renders with default
  // colors (= black) before the inline styles take effect. We mark
  // the wrapper with a flag the CSS uses to hold opacity at 0 for
  // one paint, then fade in — eliminates the black flash.
  return (
    <div
      className="v2-mermaid"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
});
